import { z } from "zod";

import { MutationProvenanceSchema } from "./mutation-provenance";

/**
 * Stable body EventBridge input transformation delivers to the automation reactor.
 * Borrows CloudEvents concepts without claiming full CloudEvents compliance.
 *
 * `id` always equals `mutation.id` for Recobra-sourced signals: the signal is
 * the persisted mutation, observed through the change stream.
 */

const SignalSubjectEntitySchema = z.enum(["task", "case", "client", "document", "message"]);
export type SignalSubjectEntity = z.infer<typeof SignalSubjectEntitySchema>;

export const AutomationSignalSchema = z.object({
  specVersion: z.literal("1"),
  id: z.string().min(1),
  type: z.string().min(1),
  occurredAt: z.iso.datetime(),
  subject: z.object({
    entity: SignalSubjectEntitySchema,
    id: z.string().min(1),
  }),
  mutation: MutationProvenanceSchema,
  data: z.unknown(),
});
export type AutomationSignal = z.infer<typeof AutomationSignalSchema>;

/** Narrows the envelope to one catalog entry's `type`, `subject.entity`, and `data`. */
export const automationSignalOf = <
  Type extends string,
  Entity extends SignalSubjectEntity,
  Data extends z.ZodType,
>(
  type: Type,
  entity: Entity,
  data: Data,
) =>
  AutomationSignalSchema.extend({
    type: z.literal(type),
    subject: z.object({ entity: z.literal(entity), id: z.string().min(1) }),
    data,
  });
